import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
// imports for hammer
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { SIDENAV_LAYOUT_ADDONS } from '@simOn/layout';
import { FullScreenService } from '@simOn/ui/sim-fullscreen';
import { MatterportTokenFetcherService } from '@simOn/matterport/auth/services';
import { MATTERPORT_TOKEN_FETCHER, MatterportConfig } from '@simlab/matterport';
import { MatterportApiConfig, MatterportApiModule } from '@simlab/matterport/api';
import { providePayPalConfig } from '@simlab/payPal';
import { AgentChatDrawerComponent } from 'feature-ai-agent';
import { LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { gitInfo } from '../git-info';

import {
  API_URL,
  AI_AGENT_ACCESS_CONTACT_EMAIL,
  AssetAttachmentLimitsToken,
  AttachmentSizeLimitsToken,
  BASE_URL,
  DEMO_APARTMENTS,
  GIT_INFO,
  Model3dSizeLimitsToken,
  NewsImgSizeLimitsToken,
  PAYU_REDIRECT,
  SIGNAL_R_API_URL,
  STAGES_PUBLIC_URL
} from '@simOn/utils/tokens';
import { appRoutes } from './app.routes';
import { provideAuthConfig } from './auth/auth-config.config';
import { provideCoreConfig } from './main/modules/core/core.config';

import { SubscriptionApiService } from '@simOn/subscription/space-limit/data-access';

import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { AssetEffects, AssetFacade, fromAsset } from '@simOn/asset';
import { SidenavService } from '@simOn/common/containers';
import {
  DevicePropertyEffects,
  DevicesEffects,
  DevicesFacade,
  fromDevices,
  fromDevicesProperty,
  fromWidget,
  WidgetEffects,
  WidgetFacade
} from '@simOn/device';
import { MatterportAuthProviderApi, RedirectAfterLoginUrl } from '@simOn/matterport/auth/tokens';
import { fromPrivileges, PrivilegesEffects, PrivilegesFacade } from '@simOn/privileges';
import { ProvidersApiService } from '@simOn/provider/connection/data-access';
import { homeyApiInterceptor } from '@simOn/provider/homey/interceptors';
import { ProviderStore } from '@simOn/provider/state';
import { PublicLinkService } from '@simOn/public-link';
import { fromRoom, RoomEffects, RoomFacade } from '@simOn/room';
import { fromScene, SceneEffects, SceneFacade } from '@simOn/scene';
import { fromSpace, SpaceEffects, SpaceFacade } from '@simOn/space';
import { fromTicket, TicketEffects, TicketFacade } from '@simOn/ticket';
import { StagesScrollDispatcher } from '@simOn/ui/scroll-observer';
import { fromUserPreferences, UserPreferencesEffect, UserPreferencesFacade } from '@simOn/user';
import { AuthService, IdentityUrl } from '@simOn/user/auth/service';
import { fromUsers, UsersEffects, UsersFacade } from '@simOn/user/last-visited/state';
import { HubName, SignalRService } from '@simOn/utils/signalR';
import { environment } from '../environments';

export function getBaseUrl() {
  const baseAddress = document.getElementsByTagName('base')[0].href;
  return baseAddress.substring(0, baseAddress.length - 1);
}
export const appConfig: ApplicationConfig = {
  providers: [
    /** The AI assistant chat lives inside the space layout (`SidenavLayoutComponent`), not at the app root. */
    { provide: SIDENAV_LAYOUT_ADDONS, useValue: [AgentChatDrawerComponent] },
    UsersFacade,
    SpaceFacade,
    DevicesFacade,
    WidgetFacade,
    UserPreferencesFacade,
    TicketFacade,
    SceneFacade,
    AssetFacade,
    PrivilegesFacade,
    ProviderStore,
    RoomFacade,
    provideStore(
      {
        [fromUsers.USERS_FEATURE_KEY]: fromUsers.usersReducer,
        [fromSpace.SPACE_FEATURE_KEY]: fromSpace.reducer,
        [fromWidget.widgetFeatureKey]: fromWidget.reducer,
        [fromDevices.deviceStructureFeatureKey]: fromDevices.reducer,
        [fromDevicesProperty.devicePropertyFeatureKey]: fromDevicesProperty.reducer,
        [fromAsset.assetFeatureKey]: fromAsset.reducer,
        [fromUserPreferences.USER_PREFERENCES_FEATURE_KEY]: fromUserPreferences.reducer,
        [fromScene.ScenesFeatureKey]: fromScene.reducer,
        [fromRoom.roomFeatureKey]: fromRoom.reducer,
        [fromTicket.ticketFeatureKey]: fromTicket.reducer,
        [fromPrivileges.privilegesFeatureKey]: fromPrivileges.reducer
      },

      {
        metaReducers: [],
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: true
        }
      }
    ),

    provideEffects(
      SpaceEffects,
      UsersEffects,
      UserPreferencesEffect,
      AssetEffects,
      WidgetEffects,
      DevicesEffects,
      DevicePropertyEffects,
      TicketEffects,
      SceneEffects,

      PrivilegesEffects,
      RoomEffects
    ),
    {
      provide: ScrollDispatcher,
      useClass: StagesScrollDispatcher
    },
    provideAnimations(),
    provideRouter(
      appRoutes(environment),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always'
      }),
      withComponentInputBinding()
    ),
    //TO DO INSIDE DEVICE IT IS
    // provideStoreConfig(),

    provideAuthConfig({
      authority: environment.authApi,
      postLogoutRedirectUri: environment.authApi,
      clientId: 'Simlab.SmarthomeJS',
      scope: 'openid email profile Simlab.SmarthomeAPI offline_access', // 'openid profile ' + your scopes
      redirectUrl: window.location.origin + `/${$localize.locale}` + '/callback',
      responseType: 'code',
      postLoginRoute: '/split-view/apartment-selection',
      silentRenew: true,
      silentRenewTimeoutInSeconds: 20,

      refreshTokenRetryInSeconds: 20,
      tokenRefreshInSeconds: 20,
      useRefreshToken: true,
      logLevel: environment.authApi.includes('dev') ? LogLevel.Error : LogLevel.None
    } as OpenIdConfiguration),
    provideCoreConfig(environment),
    AuthService,
    importProvidersFrom(MatterportApiModule),
    // payUProviders(),
    provideStoreDevtools({
      maxAge: 50, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      connectInZone: true
    }),
    { provide: IdentityUrl, useValue: environment.authApi },
    { provide: RedirectAfterLoginUrl, useValue: environment.matterportAuth.redirectAfterLoginUrl },
    {
      provide: AttachmentSizeLimitsToken,
      useValue: { imgMaxSize: environment.config.imgMaxSize, docMaxSize: environment.config.docMaxSize }
    },
    {
      provide: Model3dSizeLimitsToken,
      useValue: {
        thumbnailMaxSize: environment.config.thumbnail3dMaxSize,
        fileMaxSize: environment.config.file3dMaxSize
      }
    },
    {
      provide: NewsImgSizeLimitsToken,
      useValue: { imgMaxSize: environment.config.imgNewsMaxSize }
    },
    { provide: MatterportAuthProviderApi, useExisting: ProvidersApiService },
    { provide: BASE_URL, useFactory: getBaseUrl },
    { provide: SIGNAL_R_API_URL, useValue: environment.simlabApiSignal },
    { provide: HubName, useValue: environment.singalHubs.SimonHub },
    SignalRService,
    {
      provide: API_URL,
      useValue: environment.simlabApi
    },
    { provide: STAGES_PUBLIC_URL, useValue: environment.stages.publicUrl },
    { provide: AI_AGENT_ACCESS_CONTACT_EMAIL, useValue: environment.aiAgent.accessContactEmail },
    SidenavService,
    {
      provide: APP_INITIALIZER,
      deps: [PublicLinkService],
      useFactory: (services: PublicLinkService) => {
        // Resets a legacy public link on startup; a JWT public link session survives the refresh.
        services.removeGuid({ keepJwtSession: true });
      }
    },
    {
      provide: GIT_INFO,
      useValue: gitInfo
    },
    {
      provide: AttachmentSizeLimitsToken,
      useValue: {
        imgMaxSize: environment.config.imgMaxSize,
        docMaxSize: environment.config.docMaxSize
      }
    },
    {
      provide: AssetAttachmentLimitsToken,
      useValue: {
        imgMaxNumber: environment.config.maxImgAssetTicket,
        docMaxNumber: environment.config.maxDocAssetTicket,
        videoMaxNumber: environment.config.maxVideoAssetTicket
      }
    },
    {
      provide: DEMO_APARTMENTS,
      useValue: environment.demoApartments
    },
    FullScreenService,
    SubscriptionApiService,
    providePayPalConfig({
      clientId: environment.payment.payPal.clientId
    }),
    {
      provide: PAYU_REDIRECT,
      useValue: environment.payment.payU.redirect
    },
    // Lets the Showcase SDK renew the apartment token before it expires instead of ending the session.
    { provide: MATTERPORT_TOKEN_FETCHER, useExisting: MatterportTokenFetcherService },
    {
      provide: MatterportConfig,
      useFactory: () => {
        return {
          key: environment.matterport.sdkKey,
          hideFullscreen: true,
          hideHelp: true
        } as MatterportConfig;
      }
    },
    {
      provide: MatterportApiConfig,
      useFactory: () => {
        return {
          applicationKey: environment.matterportAuth.apiKey,
          clientId: environment.matterportAuth.clientId,
          redirectOrigin: `${window.location.origin}${environment.matterportAuth.redirectAfterLoginUrl}`,
          matterportModelApiUrl: environment.matterportAuth.apiGraphUrl,
          matterportShowcaseApiUrl: environment.matterportAuth.apiGraphUrl
        } as MatterportApiConfig;
      }
    },
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([homeyApiInterceptor]))
  ]
};
